// src/components/UI/ResponsiveTable.jsx
import React from 'react';

const ResponsiveTable = ({
  columns,
  data,
  emptyMessage = 'No hay datos disponibles',
  loading = false,
  className = '',
  onRowClick,
  striped = true,
  hover = true
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-pulse space-y-4 w-full">
          <div className="h-10 bg-gray-200 rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
          <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{emptyMessage}</h3>
        <p className="text-gray-500">Intenta cambiar los filtros o actualizar los datos.</p>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden ${className}`}>
      {/* Vista de escritorio (lg y más) */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  scope="col"
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                    column.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`${
                  striped && rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                } ${
                  hover ? 'hover:bg-gray-100 cursor-pointer transition-colors duration-150' : ''
                }`}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`px-4 py-3 whitespace-nowrap text-sm ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    } ${column.className || ''}`}
                  >
                    {column.cell ? column.cell(row) : row[column.accessor]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Vista móvil y tablet (hasta lg) */}
      <div className="lg:hidden space-y-3">
        {data.map((row, rowIndex) => (
          <div
            key={rowIndex}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
              hover ? 'hover:shadow-md transition-shadow duration-200 cursor-pointer' : ''
            }`}
          >
            <div className="p-4">
              {/* Primera fila - Información principal */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1 min-w-0">
                  {columns.slice(0, 2).map((column, index) => (
                    <div key={index} className="mb-2 last:mb-0">
                      <span className="text-xs font-medium text-gray-500 uppercase block">
                        {column.header}
                      </span>
                      <span className="text-sm font-medium text-gray-900 truncate block">
                        {column.cell ? column.cell(row) : row[column.accessor]}
                      </span>
                    </div>
                  ))}
                </div>
                {columns.length > 2 && (
                  <div className="ml-4 flex-shrink-0">
                    <div className="text-right">
                      <span className="text-xs font-medium text-gray-500 uppercase block">
                        {columns[2].header}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {columns[2].cell ? columns[2].cell(row) : row[columns[2].accessor]}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Resto de columnas - Grid responsive */}
              {columns.length > 3 && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                  {columns.slice(3).map((column, index) => (
                    <div key={index}>
                      <span className="text-xs font-medium text-gray-500 uppercase block">
                        {column.header}
                      </span>
                      <span className="text-sm font-medium text-gray-900 block">
                        {column.cell ? column.cell(row) : row[column.accessor]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResponsiveTable;